import * as runtime from '../runtime';
import type {
  CreateListingRequest,
  CreateListingResponse,
  ErrorResource,
  FindListingsResponse,
  GetAllSellersResponse,
  ListingCountResponse,
  ListingDetails,
  ListingRequest,
  NearbyListingsResponse,
  UpdateListingRequest,
} from '../models';

export interface CreateListingOperationRequest {
  createListingRequest: CreateListingRequest;
}

export interface DeleteListingRequest {
  id: number;
}

export interface FindAllListingsRequest {
  page?: number;
  listingTypeIds?: string;
}

export interface FindListingsRequest {
  listingRequest: ListingRequest;
}

export interface FindListingsByCityRequest {
  cityId: string;
  page?: number;
  listingTypeIds?: string;
}

export interface FindListingsBySellerIdRequest {
  sellerId: string;
  page?: number;
  listingTypeIds?: string;
}

export interface GetListingDetailsRequest {
  listingId: string;
}

export interface GetNearbyListingsRequest {
  listingId: number;
}

export interface ListingCountRequest {
  listingRequest: ListingRequest;
}

export interface UpdateListingOperationRequest {
  id: number;
  updateListingRequest: UpdateListingRequest;
}

/**
 *
 */
export class ListingApi extends runtime.BaseAPI {
  async createListingRaw(
    requestParameters: CreateListingOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<CreateListingResponse>> {
    if (
      requestParameters.createListingRequest === null ||
      requestParameters.createListingRequest === undefined
    ) {
      throw new runtime.RequiredError(
        'createListingRequest',
        'Required parameter requestParameters.createListingRequest was null or undefined when calling createListing.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/listing`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.createListingRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Creates a new real estate listing with detailed property information such as location, area, pricing, and additional property attributes.
   * Create a new property listing
   */
  async createListing(
    requestParameters: CreateListingOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<CreateListingResponse> {
    const response = await this.createListingRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async deleteListingRaw(
    requestParameters: DeleteListingRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<void>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        'id',
        'Required parameter requestParameters.id was null or undefined when calling deleteListing.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/listing/{id}`.replace(
          `{${'id'}}`,
          encodeURIComponent(String(requestParameters.id)),
        ),
        method: 'DELETE',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.VoidApiResponse(response);
  }

  /**
   * Permanently deletes a property listing and all associated data. This operation cannot be undone.
   * Delete a property listing
   */
  async deleteListing(
    requestParameters: DeleteListingRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<void> {
    await this.deleteListingRaw(requestParameters, initOverrides);
  }

  async findAllListingsRaw(
    requestParameters: FindAllListingsRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<FindListingsResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    if (requestParameters.page !== undefined) {
      queryParameters['page'] = requestParameters.page;
    }

    if (requestParameters.listingTypeIds !== undefined) {
      queryParameters['listingTypeIds'] = requestParameters.listingTypeIds;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/listing/all`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Find all listings regardless of city or seller
   */
  async findAllListings(
    requestParameters: FindAllListingsRequest = {},
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<FindListingsResponse> {
    const response = await this.findAllListingsRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async findListingsRaw(
    requestParameters: FindListingsRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<FindListingsResponse>> {
    if (
      requestParameters.listingRequest === null ||
      requestParameters.listingRequest === undefined
    ) {
      throw new runtime.RequiredError(
        'listingRequest',
        'Required parameter requestParameters.listingRequest was null or undefined when calling findListings.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/listing/findlistings`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.listingRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Find listings based on a set of filters
   */
  async findListings(
    requestParameters: FindListingsRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<FindListingsResponse> {
    const response = await this.findListingsRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async findListingsByCityRaw(
    requestParameters: FindListingsByCityRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<FindListingsResponse>> {
    if (
      requestParameters.cityId === null ||
      requestParameters.cityId === undefined
    ) {
      throw new runtime.RequiredError(
        'cityId',
        'Required parameter requestParameters.cityId was null or undefined when calling findListingsByCity.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    if (requestParameters.page !== undefined) {
      queryParameters['page'] = requestParameters.page;
    }

    if (requestParameters.listingTypeIds !== undefined) {
      queryParameters['listingTypeIds'] = requestParameters.listingTypeIds;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/listing/city/{cityId}`.replace(
          `{${'cityId'}}`,
          encodeURIComponent(String(requestParameters.cityId)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Find listings based on city ID
   */
  async findListingsByCity(
    requestParameters: FindListingsByCityRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<FindListingsResponse> {
    const response = await this.findListingsByCityRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async findListingsBySellerIdRaw(
    requestParameters: FindListingsBySellerIdRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<FindListingsResponse>> {
    if (
      requestParameters.sellerId === null ||
      requestParameters.sellerId === undefined
    ) {
      throw new runtime.RequiredError(
        'sellerId',
        'Required parameter requestParameters.sellerId was null or undefined when calling findListingsBySellerId.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    if (requestParameters.page !== undefined) {
      queryParameters['page'] = requestParameters.page;
    }

    if (requestParameters.listingTypeIds !== undefined) {
      queryParameters['listingTypeIds'] = requestParameters.listingTypeIds;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/listing/sellers/{sellerId}`.replace(
          `{${'sellerId'}}`,
          encodeURIComponent(String(requestParameters.sellerId)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Find listings based on seller ID
   */
  async findListingsBySellerId(
    requestParameters: FindListingsBySellerIdRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<FindListingsResponse> {
    const response = await this.findListingsBySellerIdRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async getAllSellersWithListingCountsRaw(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetAllSellersResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/listing/sellers`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Returns all seller details along with their listing count.
   * Get all sellers with number of listings
   */
  async getAllSellersWithListingCounts(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetAllSellersResponse> {
    const response = await this.getAllSellersWithListingCountsRaw(
      initOverrides,
    );
    return await response.value();
  }

  async getListingDetailsRaw(
    requestParameters: GetListingDetailsRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<ListingDetails>> {
    if (
      requestParameters.listingId === null ||
      requestParameters.listingId === undefined
    ) {
      throw new runtime.RequiredError(
        'listingId',
        'Required parameter requestParameters.listingId was null or undefined when calling getListingDetails.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/listing/{listingId}`.replace(
          `{${'listingId'}}`,
          encodeURIComponent(String(requestParameters.listingId)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Gets details for a specific listing.
   */
  async getListingDetails(
    requestParameters: GetListingDetailsRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<ListingDetails> {
    const response = await this.getListingDetailsRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async getNearbyListingsRaw(
    requestParameters: GetNearbyListingsRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<NearbyListingsResponse>> {
    if (
      requestParameters.listingId === null ||
      requestParameters.listingId === undefined
    ) {
      throw new runtime.RequiredError(
        'listingId',
        'Required parameter requestParameters.listingId was null or undefined when calling getNearbyListings.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/listing/{listingId}/nearby`.replace(
          `{${'listingId'}}`,
          encodeURIComponent(String(requestParameters.listingId)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Get nearby listings with city → province → state fallback
   */
  async getNearbyListings(
    requestParameters: GetNearbyListingsRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<NearbyListingsResponse> {
    const response = await this.getNearbyListingsRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async listingCountRaw(
    requestParameters: ListingCountRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<ListingCountResponse>> {
    if (
      requestParameters.listingRequest === null ||
      requestParameters.listingRequest === undefined
    ) {
      throw new runtime.RequiredError(
        'listingRequest',
        'Required parameter requestParameters.listingRequest was null or undefined when calling listingCount.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/listing/count`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.listingRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Listing count based on user applied filters
   */
  async listingCount(
    requestParameters: ListingCountRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<ListingCountResponse> {
    const response = await this.listingCountRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async updateListingRaw(
    requestParameters: UpdateListingOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<ListingDetails>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        'id',
        'Required parameter requestParameters.id was null or undefined when calling updateListing.',
      );
    }

    if (
      requestParameters.updateListingRequest === null ||
      requestParameters.updateListingRequest === undefined
    ) {
      throw new runtime.RequiredError(
        'updateListingRequest',
        'Required parameter requestParameters.updateListingRequest was null or undefined when calling updateListing.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/listing/{id}`.replace(
          `{${'id'}}`,
          encodeURIComponent(String(requestParameters.id)),
        ),
        method: 'PUT',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.updateListingRequest,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Updates an existing real estate listing with modified property information such as location, area, pricing, and additional property attributes.
   * Update an existing property listing
   */
  async updateListing(
    requestParameters: UpdateListingOperationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<ListingDetails> {
    const response = await this.updateListingRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }
}
